import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import PageHeader from '../../components/shell/PageHeader';
import IconButton from '../../components/ui/IconButton';
import CollapsibleCard from '../../components/ui/CollapsibleCard';
import StatusPill from '../../components/ui/StatusPill';
import { Select } from '../../components/ui/Field';
import { SkeletonBlock } from '../../components/ui/Skeleton';
import {
  getAiUserMessageHistory,
  getAiUserTransactionHistory,
  getSelectedUserDetails,
} from '../../actions/aiChatAction';
import { formatDateTime, inr, number, phone } from '../../helpers/format';
import './aichat.css';

/* meta_data.plan_type is 'time' for a flow and 'credit' for credits. */
const PLAN_LABEL = { time: 'Flow', credit: 'Credits' };

/** 'female' -> 'Female'. Gender is stored free-form on users.properties. */
function titleCase(value) {
  if (!value) return '—';
  return String(value).replace(/[_-]+/g, ' ').trim().replace(/\b\w/g, (c) => c.toUpperCase());
}

export default function AiTranscript() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [summary, setSummary] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [day, setDay] = useState('');

  // The list screen stores the ordered users, so prev/next can walk the same order
  // the admin was just looking at rather than re-querying.
  const aiUsers = useSelector((s) => s.aiChat?.aiUsers) || [];
  const index = aiUsers.findIndex((u) => String(u.user_id) === String(id));
  const prev = index > 0 ? aiUsers[index - 1] : null;
  const next = index >= 0 && index < aiUsers.length - 1 ? aiUsers[index + 1] : null;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      getSelectedUserDetails(id),
      getAiUserMessageHistory(dispatch, id),
      getAiUserTransactionHistory(dispatch, id),
    ])
      .then(([details, history, txns]) => {
        if (cancelled) return;
        setUser(details || null);
        setMessages(history?.messages || []);
        setSummary(history?.summary || []);
        setTransactions(Array.isArray(txns) ? txns : []);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id, dispatch]);

  /* Oldest first reads like a conversation; the API returns newest first. */
  const ordered = useMemo(() => {
    const list = [...messages].reverse();
    if (!day) return list;
    return list.filter((m) => (m.timestamp || '').slice(0, 10) === day);
  }, [messages, day]);

  const purchaseValue = useMemo(
    () => transactions.reduce((n, t) => n + Math.abs(t.amount || 0), 0),
    [transactions]
  );

  const flows = useMemo(
    () => transactions.filter((t) => t.meta_data?.plan_type === 'time').length,
    [transactions]
  );
  const credits = useMemo(
    () => transactions.filter((t) => t.meta_data?.plan_type === 'credit').length,
    [transactions]
  );

  const totals = useMemo(
    () =>
      summary.reduce(
        (acc, d) => ({
          user: acc.user + (d.user_message_count || 0),
          assistant: acc.assistant + (d.assistant_message_count || 0),
        }),
        { user: 0, assistant: 0 }
      ),
    [summary]
  );

  // Phone lives in the Customer card now. It used to lead this line, which meant a
  // user without a number opened their own header with a dash.
  const metaLine = user
    ? [
        `${number(transactions.length)} transactions`,
        `${inr(purchaseValue)} purchased`,
        `${number(user.ai_access?.available_credits ?? 0)} credits left`,
      ].join(' · ')
    : '';

  return (
    <>
      <PageHeader
        section="AI"
        title={user?.properties?.user_name || user?.email || 'Transcript'}
        subtitle={metaLine}
        back="AI Chat"
        backTo="/ai-chat"
        actions={
          <>
            <IconButton
              icon="bx-chevron-left"
              bordered
              label="Previous user"
              disabled={!prev}
              onClick={() => prev && navigate(`/ai-chat/${prev.user_id}`)}
            />
            <IconButton
              icon="bx-chevron-right"
              bordered
              label="Next user"
              disabled={!next}
              onClick={() => next && navigate(`/ai-chat/${next.user_id}`)}
            />
          </>
        }
      />

      <div className="sv-screen-body">
        <div className="sv-transcript">
          <section className="sv-card">
            <header className="sv-transcript__head">
              <span className="sv-card__title">Conversation</span>
              <Select
                options={[
                  { value: '', label: 'All days' },
                  ...summary.map((d) => ({ value: d.date, label: d.date })),
                ]}
                value={day}
                onChange={(e) => setDay(e.target.value)}
              />
            </header>

            <div className="sv-transcript__thread">
              {loading &&
                Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} style={{ alignSelf: i % 2 ? 'flex-end' : 'flex-start', width: '60%' }}>
                    <SkeletonBlock width="100%" height={44} radius={14} />
                  </div>
                ))}

              {!loading && ordered.length === 0 && (
                <p className="sv-transcript__empty">No messages in this period.</p>
              )}

              {!loading &&
                ordered.map((m) => (
                  <div key={m.id} className={`sv-msg ${m.role === 'user' ? 'is-user' : 'is-vani'}`}>
                    <div className="sv-msg__body">{m.content}</div>
                    <div className="sv-msg__meta">
                      {formatDateTime(m.timestamp)}
                      {m.is_helpful === true && <i className="bx bx-like" title="Marked helpful" />}
                      {m.is_helpful === false && <i className="bx bx-dislike" title="Marked unhelpful" />}
                    </div>
                  </div>
                ))}
            </div>
          </section>

          <aside className="sv-transcript__rail">
            <CollapsibleCard title="Customer">
              {[
                ['Name', user?.properties?.user_name || '—'],
                ['Email', user?.email || '—'],
                ['Phone', phone(user?.phone_number)],
                ['Gender', titleCase(user?.gender)],
              ].map(([label, value]) => (
                <div className="sv-drawer-field" key={label}>
                  <span className="sv-drawer-field__label">{label}</span>
                  <span className="sv-drawer-field__value">{value}</span>
                </div>
              ))}
            </CollapsibleCard>

            <CollapsibleCard title="Usage">
              {[
                ['Total transactions', number(transactions.length)],
                ['Purchase value', inr(purchaseValue)],
                ['Flows purchased', number(flows)],
                ['Credits purchased', number(credits)],
                ['Credits left', number(user?.ai_access?.available_credits ?? 0)],
                ['Flows left', number(user?.ai_access?.flash_available ?? 0)],
                ['User messages', number(totals.user)],
                ['Vani replies', number(totals.assistant)],
                ['Created at', formatDateTime(user?.created_at)],
                ['Latest message', formatDateTime(messages[0]?.timestamp)],
              ].map(([label, value]) => (
                <div className="sv-drawer-field" key={label}>
                  <span className="sv-drawer-field__label">{label}</span>
                  <span className="sv-drawer-field__value sv-mono">{value}</span>
                </div>
              ))}
            </CollapsibleCard>

            <CollapsibleCard
              title="Transaction history"
              meta={transactions.length ? `${number(transactions.length)} · ${inr(purchaseValue)}` : null}
            >
              {transactions.length === 0 && (
                <p className="sv-txn-empty">No AI purchases yet.</p>
              )}

              {transactions.length > 0 && (
                <div className="sv-txn-list">
                  {transactions.map((t) => (
                    <div className="sv-txn-row" key={t.id}>
                      <div className="sv-txn-row__top">
                        <span className="sv-txn-row__title">
                          {t.title || PLAN_LABEL[t.meta_data?.plan_type] || 'AI purchase'}
                        </span>
                        {/* Purchases are stored as negative amounts; the sign is
                            noise on a screen that only lists purchases. */}
                        <span className="sv-txn-row__amount sv-mono">
                          {inr(Math.abs(t.amount || 0))}
                        </span>
                      </div>
                      <div className="sv-txn-row__bottom">
                        <span className="sv-txn-row__date sv-mono">
                          {formatDateTime(t.created_at)}
                          {PLAN_LABEL[t.meta_data?.plan_type]
                            ? ` · ${PLAN_LABEL[t.meta_data.plan_type]}`
                            : ''}
                        </span>
                        {t.status && <StatusPill status={t.status} />}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CollapsibleCard>

            {summary.length > 0 && (
              <CollapsibleCard title="By day" meta={`${summary.length} days`}>
                {summary.map((d) => (
                  <button
                    type="button"
                    key={d.date}
                    className={`sv-day-row ${day === d.date ? 'is-active' : ''}`}
                    onClick={() => setDay(day === d.date ? '' : d.date)}
                  >
                    <span className="sv-mono">{d.date}</span>
                    <span className="sv-day-row__counts">
                      {d.user_message_count} / {d.assistant_message_count}
                      {d.like_count > 0 && <i className="bx bx-like" />}
                      {d.dislike_count > 0 && <i className="bx bx-dislike" />}
                    </span>
                  </button>
                ))}
              </CollapsibleCard>
            )}
          </aside>
        </div>
      </div>
    </>
  );
}
